import React, { useState } from 'react';
import styles from './CCPACompliance.module.css';
import api from '../../api/axois';

const CCPACompliance = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    requestType: 'access',
    additionalInfo: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Create message from request type and additional info
      const requestTypeLabels = {
        'access': 'Request Access to My Personal Data',
        'rectification': 'Request Correction of My Data',
        'erasure': 'Request Deletion of My Personal Data',
        'object': 'Object to Processing of My Data',
        'portability': 'Request Data Transfer/Portability',
        'restrict': 'Request Restriction of Processing',
        'withdraw': 'Withdraw My Consent'
      };
      
      const message = `NDPR Request Type: ${requestTypeLabels[formData.requestType]}\n\nAdditional Information:\n${formData.additionalInfo || 'None provided'}`;
      
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        message: message,
        source: 'ndpr_request',
      };
      
      const res = await api.post('/contact', payload);
      setSuccess(res.data.message || 'NDPR request submitted successfully. We will process your request within 30 days.');
      setIsSubmitted(true);
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        requestType: 'access',
        additionalInfo: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || (err.response?.data?.errors ? JSON.stringify(err.response.data.errors) : 'Failed to submit NDPR request. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted && success) {
    return (
      <div className={styles.ccpaContainer}>
        <div className={styles.ccpaContent}>
          <div className={styles.successMessage}>
            <h1>NDPR Request Submitted Successfully</h1>
            <p>
              {success}
            </p>
            <p>
              If you have any questions, please contact us at <strong>info@aurelia-apartments.com</strong>
            </p>
            <button 
              className={styles.backButton}
              onClick={() => {
                setIsSubmitted(false);
                setSuccess('');
              }}
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.ccpaContainer}>
      <div className={styles.ccpaContent}>
        <h1>Nigerian Data Protection Rights (NDPR)</h1>
        <p className={styles.ccpaSubtitle}>
          Exercise your rights under the Nigeria Data Protection Regulation
        </p>

        <section className={styles.ccpaSection}>
          <h2>Your NDPR Rights</h2>
          <p>
            As a Nigerian resident or data subject, you have the following rights regarding your personal information:
          </p>
          
          <div className={styles.rightsGrid}>
            <div className={styles.rightItem}>
              <h3>Right to Information</h3>
              <p>Be informed about how your personal data is being processed and for what purpose.</p>
            </div>
            
            <div className={styles.rightItem}>
              <h3>Right of Access</h3>
              <p>Request access to your personal data and information about how it's being processed.</p>
            </div>
            
            <div className={styles.rightItem}>
              <h3>Right to Rectification</h3>
              <p>Request correction of inaccurate or incomplete personal data.</p>
            </div>
            
            <div className={styles.rightItem}>
              <h3>Right to Erasure</h3>
              <p>Request deletion of your personal data under certain circumstances.</p>
            </div>

            <div className={styles.rightItem}>
              <h3>Right to Object</h3>
              <p>Object to the processing of your personal data for direct marketing or other purposes.</p>
            </div>
            
            <div className={styles.rightItem}>
              <h3>Right to Data Portability</h3>
              <p>Request transfer of your data to another service provider in a structured format.</p>
            </div>
          </div>
        </section>

        <section className={styles.ccpaSection}>
          <h2>Information We Process</h2>
          <p>Over the past 12 months, we have processed the following categories of personal information:</p>
          <ul>
            <li><strong>Identifiers:</strong> Name, email, phone number, IP address</li>
            <li><strong>Commercial Information:</strong> Booking history, payment information</li>
            <li><strong>Internet Activity:</strong> Website usage, cookies, browsing patterns</li>
            <li><strong>Geolocation Data:</strong> General location information</li>
            <li><strong>Professional Information:</strong> Business contact details (if applicable)</li>
            <li><strong>Biometric Information:</strong> None collected</li>
          </ul>
        </section>

        <section className={styles.ccpaSection}>
          <h2>How We Use Your Information</h2>
          <p>We process your personal information for the following legitimate business purposes:</p>
          <ul>
            <li>Processing reservations and providing accommodation services</li>
            <li>Customer service and support</li>
            <li>Marketing and promotional activities (with your consent)</li>
            <li>Website analytics and improvement</li>
            <li>Fraud prevention and security</li>
            <li>Legal and regulatory compliance</li>
            <li>Business administration and record-keeping</li>
          </ul>
        </section>

        <section className={styles.ccpaSection}>
          <h2>Data Sharing and Processing</h2>
          <p>
            We <strong>do not sell personal information</strong> to third parties. We may share your data with service providers 
            who process data on our behalf, such as payment processors, email service providers, and analytics services. 
            All sharing is done with appropriate data protection agreements in place.
          </p>
        </section>

        <section className={styles.ccpaSection}>
          <h2>Submit Your NDPR Request</h2>
          
          <form onSubmit={handleSubmit} className={styles.ccpaForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="requestType">Request Type *</label>
              <select
                id="requestType"
                name="requestType"
                value={formData.requestType}
                onChange={handleInputChange}
                required
              >
                <option value="access">Request Access to My Personal Data</option>
                <option value="rectification">Request Correction of My Data</option>
                <option value="erasure">Request Deletion of My Personal Data</option>
                <option value="object">Object to Processing of My Data</option>
                <option value="portability">Request Data Transfer/Portability</option>
                <option value="restrict">Request Restriction of Processing</option>
                <option value="withdraw">Withdraw My Consent</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="additionalInfo">Additional Information (Optional)</label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleInputChange}
                rows="4"
                placeholder="Please provide any additional details about your request..."
              />
            </div>

            <div className={styles.verificationNotice}>
              <p>
                <strong>Identity Verification:</strong> To protect your privacy, we may need to verify your identity 
                before processing your request. We will contact you using the information provided above in accordance 
                with NDPR requirements.
              </p>
            </div>

            {error && (
              <div style={{ 
                background: '#fee', 
                border: '1px solid #fcc', 
                padding: '12px', 
                borderRadius: '6px', 
                color: '#c33',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </form>
        </section>

        <section className={styles.ccpaSection}>
          <h2>Response Timeline</h2>
          <p>
            We will acknowledge receipt of your request within 10 days and provide a substantive response within 30 days 
            as required under the NDPR. In complex cases, we may extend this period by an additional 30 days, 
            in which case we will notify you of the extension and the reasons for it.
          </p>
        </section>

        <section className={styles.ccpaSection}>
          <h2>Contact Information</h2>
          <p>For questions about this NDPR policy or to submit requests by other means:</p>
          
          <div className={styles.contactInfo}>
            <div className={styles.contactItem}>
              <strong>Email:</strong> info@aurelia-apartments.com
            </div>
            <div className={styles.contactItem}>
              <strong>Phone:</strong> +2348165028212
            </div>
            <div className={styles.contactItem}>
              <strong>Mail:</strong> 2 Bukar Dipcharima close off Teslim Elias street off TOS Benson Street Utako Abuja, Nigeria
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CCPACompliance;

